import React, { useState } from 'react';
import ValueFields from './ValueFields.jsx';
import {
  SUB_KEY_OPTIONS,
  OVERRIDE_TIERS,
  TIER_LABEL,
  valueTemplate,
  formatValue,
} from './constants.js';
import { setConfigValue, clearConfigValue } from '../../state/index.js';

const todayISO = () => new Date().toISOString().slice(0, 10);

function factoryFor(param, subKey) {
  const dv = param.default_value;
  if (subKey != null) return dv?.[subKey] ?? null;
  return dv ?? null;
}

// One parameter: sub-key-aware base (Global) editor + sparse override expander.
export default function ParameterCard({ param, categories, configValues, onChanged }) {
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showExc, setShowExc] = useState(false);
  const [editingBase, setEditingBase] = useState(null); // subKey or '__single__'
  const [baseDraft, setBaseDraft] = useState(null);
  const [addingExc, setAddingExc] = useState(false);
  const [excDraft, setExcDraft] = useState(null);
  const [editingExcId, setEditingExcId] = useState(null);

  const template = valueTemplate(param);
  const subKeys = SUB_KEY_OPTIONS[param.id] || [null];
  const isInternal = param.family === 'internal';

  const rows = configValues.filter((r) => r.param_id === param.id);
  const globalRow = (subKey) =>
    rows.find((r) => r.scope_tier === 'global' && (r.sub_key ?? null) === (subKey ?? null));
  const exceptions = rows
    .filter((r) => r.scope_tier !== 'global')
    .sort((a, b) => a.scope_tier.localeCompare(b.scope_tier) || (a.scope_key || '').localeCompare(b.scope_key || ''));

  async function guard(fn) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  // ---- base (Global) value ----
  function startEditBase(subKey) {
    const key = subKey ?? '__single__';
    const gr = globalRow(subKey);
    setBaseDraft(gr ? { ...gr.value } : { ...factoryFor(param, subKey) });
    setEditingBase(key);
  }
  async function saveBase(subKey) {
    await guard(() =>
      setConfigValue(
        { param_id: param.id, scope_tier: 'global', scope_key: null, sub_key: subKey ?? null },
        baseDraft,
      ),
    );
    setEditingBase(null);
  }
  async function resetBase(subKey) {
    await guard(() =>
      clearConfigValue({
        param_id: param.id,
        scope_tier: 'global',
        scope_key: null,
        sub_key: subKey ?? null,
      }),
    );
    setEditingBase(null);
  }

  // ---- exceptions ----
  function startAddExc() {
    setExcDraft({
      scope_tier: 'category',
      scope_key: categories[0]?.letter || '',
      sub_key: subKeys[0] ?? null,
      value: { ...factoryFor(param, subKeys[0] ?? null) },
      note: '',
      expires_on: '',
    });
    setEditingExcId(null);
    setAddingExc(true);
  }
  function startEditExc(row) {
    setExcDraft({
      scope_tier: row.scope_tier,
      scope_key: row.scope_key,
      sub_key: row.sub_key,
      value: { ...row.value },
      note: row.note || '',
      expires_on: row.expires_on || '',
    });
    setEditingExcId(row.id);
    setAddingExc(true);
  }
  async function saveExc() {
    const d = excDraft;
    if (!d.scope_key) {
      setErr('Scope id is required for an exception.');
      return;
    }
    await guard(() =>
      setConfigValue(
        {
          param_id: param.id,
          scope_tier: d.scope_tier,
          scope_key: d.scope_key,
          sub_key: d.sub_key ?? null,
        },
        d.value,
        { note: d.note || null, expires_on: d.expires_on || null },
      ),
    );
    setAddingExc(false);
    setExcDraft(null);
    setEditingExcId(null);
  }
  async function removeExc(row) {
    await guard(() =>
      clearConfigValue({
        param_id: param.id,
        scope_tier: row.scope_tier,
        scope_key: row.scope_key,
        sub_key: row.sub_key ?? null,
      }),
    );
  }

  const catLabel = (letter) => {
    const c = categories.find((x) => x.letter === letter);
    return c ? `${c.letter} — ${c.name}` : letter;
  };

  return (
    <div className="param-card">
      <div className="param-head">
        <div>
          <span className="param-id">{param.id}</span>
          <span className="param-name">{param.name}</span>
        </div>
        <div className="param-tier">{TIER_LABEL[param.native_tier] || param.native_tier}{param.unit ? ` · ${param.unit}` : ''}</div>
      </div>

      {param.description && <div className="param-desc">{param.description}</div>}

      {isInternal && (
        <div className="internal-meta">
          <span><strong>Feedback signal:</strong> {param.feedback_signal || '—'}</span>
          <span><strong>Review:</strong> {param.review_cadence || '—'}</span>
        </div>
      )}

      {/* base values (one per sub-key when sub-keyed) */}
      <div className="base-values">
        {subKeys.map((subKey) => {
          const gr = globalRow(subKey);
          const effective = gr ? gr.value : factoryFor(param, subKey);
          const source = gr ? 'Global' : 'Factory default';
          const editKey = subKey ?? '__single__';
          const editing = editingBase === editKey;
          return (
            <div key={editKey} className="base-row">
              <div className="base-label">
                {subKey ? <span className="subkey">{subKey}</span> : null}
                <span className="base-value">{formatValue(effective)}</span>
                <span className="base-source">{source}</span>
              </div>
              {!editing ? (
                <div className="base-actions">
                  <button className="btn btn-small" disabled={busy} onClick={() => startEditBase(subKey)}>
                    Edit
                  </button>
                  {gr && (
                    <button className="btn btn-small btn-ghost" disabled={busy} onClick={() => resetBase(subKey)}>
                      Reset to factory
                    </button>
                  )}
                </div>
              ) : (
                <div className="editor">
                  <ValueFields template={template} value={baseDraft} onChange={setBaseDraft} />
                  <div className="editor-actions">
                    <button className="btn btn-small" disabled={busy} onClick={() => saveBase(subKey)}>
                      Save
                    </button>
                    <button className="btn btn-small btn-ghost" disabled={busy} onClick={() => setEditingBase(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* exceptions */}
      <div className="exceptions">
        <button className="exc-toggle" onClick={() => setShowExc((s) => !s)}>
          {showExc ? '▾' : '▸'} Exceptions ({exceptions.length})
        </button>
        {showExc && (
          <div className="exc-body">
            {exceptions.length === 0 && <div className="muted small">No overrides. Global applies everywhere.</div>}
            {exceptions.map((row) => {
              const expired = row.expires_on && row.expires_on < todayISO();
              return (
                <div key={row.id} className={expired ? 'exc-row expired' : 'exc-row'}>
                  <div className="exc-scope">
                    <span className="tier-badge">{TIER_LABEL[row.scope_tier]}</span>
                    <span>{row.scope_tier === 'category' ? catLabel(row.scope_key) : row.scope_key}</span>
                    {row.sub_key && <span className="subkey">{row.sub_key}</span>}
                  </div>
                  <div className="exc-value">{formatValue(row.value)}</div>
                  <div className="exc-meta">
                    {row.note && <span className="note">{row.note}</span>}
                    {row.expires_on && (
                      <span className={expired ? 'expiry expired-text' : 'expiry'}>
                        {expired ? 'expired ' : 'expires '} {row.expires_on}
                      </span>
                    )}
                  </div>
                  <div className="exc-actions">
                    <button className="btn btn-small" disabled={busy} onClick={() => startEditExc(row)}>Edit</button>
                    <button className="btn btn-small btn-ghost" disabled={busy} onClick={() => removeExc(row)}>Remove</button>
                  </div>
                </div>
              );
            })}

            {!addingExc && (
              <button className="btn btn-small" disabled={busy} onClick={startAddExc}>+ Add exception</button>
            )}

            {addingExc && excDraft && (
              <div className="exc-form">
                <div className="exc-form-row">
                  <label className="field">
                    <span className="field-label">tier</span>
                    <select
                      value={excDraft.scope_tier}
                      disabled={editingExcId != null}
                      onChange={(e) => setExcDraft({ ...excDraft, scope_tier: e.target.value, scope_key: e.target.value === 'category' ? (categories[0]?.letter || '') : '' })}
                    >
                      {OVERRIDE_TIERS.map((t) => (
                        <option key={t} value={t}>{TIER_LABEL[t]}</option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span className="field-label">{excDraft.scope_tier === 'category' ? 'category' : `${excDraft.scope_tier} id`}</span>
                    {excDraft.scope_tier === 'category' ? (
                      <select
                        value={excDraft.scope_key}
                        disabled={editingExcId != null}
                        onChange={(e) => setExcDraft({ ...excDraft, scope_key: e.target.value })}
                      >
                        {categories.map((c) => (
                          <option key={c.letter} value={c.letter}>{c.letter} — {c.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder={excDraft.scope_tier === 'dc' ? 'e.g. F013519-Violet' : 'e.g. F8152'}
                        value={excDraft.scope_key}
                        disabled={editingExcId != null}
                        onChange={(e) => setExcDraft({ ...excDraft, scope_key: e.target.value })}
                      />
                    )}
                  </label>
                  {subKeys[0] != null && (
                    <label className="field">
                      <span className="field-label">status</span>
                      <select
                        value={excDraft.sub_key ?? ''}
                        disabled={editingExcId != null}
                        onChange={(e) => setExcDraft({ ...excDraft, sub_key: e.target.value })}
                      >
                        {subKeys.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
                <ValueFields template={template} value={excDraft.value} onChange={(v) => setExcDraft({ ...excDraft, value: v })} />
                <div className="exc-form-row">
                  <label className="field grow">
                    <span className="field-label">note (optional)</span>
                    <input type="text" value={excDraft.note} onChange={(e) => setExcDraft({ ...excDraft, note: e.target.value })} />
                  </label>
                  <label className="field">
                    <span className="field-label">expiry (optional)</span>
                    <input type="date" value={excDraft.expires_on} onChange={(e) => setExcDraft({ ...excDraft, expires_on: e.target.value })} />
                  </label>
                </div>
                <div className="editor-actions">
                  <button className="btn btn-small" disabled={busy} onClick={saveExc}>Save exception</button>
                  <button className="btn btn-small btn-ghost" disabled={busy} onClick={() => { setAddingExc(false); setExcDraft(null); setEditingExcId(null); }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {err && <div className="param-err">{err}</div>}
    </div>
  );
}
